import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  HeadBar,
  SubNavigationBar,
  SubNavigationItem,
} from '../shared/components'
import { SettingsIcon } from '@/app/features/icons/Settings'
import { SETTINGS_ROUTES } from './routes-typed'
import clx from 'classnames'
import { useCanManageIpAllowList } from '@/modules/shared/query-client/useCompanyIpAllowList'

type Props = {
  children: React.ReactNode
  companyId: string
}

// Translation-key → typed route — drives the sidebar item list. Each route's
// `buildPath({ companyId })` is compile-time checked against `SETTINGS_ROUTES`,
// and the runtime validator coerces `companyId` to number.
const SETTINGS_TABS: ReadonlyArray<{
  key: keyof typeof SETTINGS_ROUTES
  label: string
}> = [
  { key: 'basic', label: 'configuration-menu.basic' },
  { key: 'businessTrip', label: 'configuration-menu.business-trips' },
  { key: 'businessTripRates', label: 'configuration-menu.business-trip-rates' },
  { key: 'timesheetBasic', label: 'configuration-menu.timesheet-basic' },
  { key: 'timesheetAdditional', label: 'configuration-menu.timesheet-additional' },
  { key: 'payrollModule', label: 'configuration-menu.payroll-module' },
  { key: 'minimumWage', label: 'configuration-menu.minimum-wages' },
  { key: 'violation', label: 'configuration-menu.violations' },
  { key: 'exchangeRate', label: 'configuration-menu.exchange-rates' },
  { key: 'country', label: 'configuration-menu.countries' },
  { key: 'printout', label: 'configuration-menu.printouts' },
  { key: 'alertReminders', label: 'configuration-menu.reminders' },
  { key: 'gps', label: 'configuration-menu.gps' },
  { key: 'scanner', label: 'configuration-menu.scanner' },
  { key: 'integration', label: 'configuration-menu.integration' },
  { key: 'emailConfiguration', label: 'configuration-menu.email-configuration' },
]

const IP_ACCESS_TAB = { key: 'ipAccess', label: 'ipAllowList.tab' } as const

export const SettingsLayout = ({ children, companyId }: Props) => {
  const { t } = useLocalize()
  const cid = companyId
  const tabs = useCanManageIpAllowList(companyId) ? [...SETTINGS_TABS, IP_ACCESS_TAB] : SETTINGS_TABS
  const fullScreen = false
  const heightScreen = true
  const baseClasses = clx(
    'module-settings tw-flex tw-flex-[1] tw-flex-col tw-text-content-base tw-bg-base-100 tw-min-w-[900px] tw-max-w-full',
    {
      'tw-h-screen': fullScreen,
      'tw-padding-4 tw-mt-1 tw-mb-1 tw-rounded-xl tw-border tw-border-base-300':
        !fullScreen,
      'tw-max-h-[calc(100vh-8px)]': !fullScreen && heightScreen,
      'tw-min-h-[calc(100vh-8px)]': !fullScreen && !heightScreen,
    },
  )
  return (
    <div className={baseClasses}>
      <HeadBar title={t('configuration-menu.title')} icon={<SettingsIcon />} />

      <SubNavigationBar>
        {tabs.map(({ key, label }) => (
          <SubNavigationItem
            key={key}
            to={SETTINGS_ROUTES[key].buildPath({ companyId: cid })}
          >
            {t(label)}
          </SubNavigationItem>
        ))}
      </SubNavigationBar>

      <main
        className={`tw-flex tw-h-full tw-w-full tw-flex-1 ${heightScreen ? 'tw-max-h-screen tw-flex-col tw-overflow-hidden' : ''} `}
      >
        {children}
      </main>
    </div>
  )
}
